import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Chip, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { listarCursos, deletarCurso } from '../../api/cursos';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

const STATUS_COLOR = {
  rascunho: 'default',
  aberto: 'success',
  andamento: 'info',
  concluido: 'default',
  cancelado: 'error'
};

const STATUS_LABEL = {
  rascunho: 'Rascunho',
  aberto: 'Aberto',
  andamento: 'Em Andamento',
  concluido: 'Concluído',
  cancelado: 'Cancelado'
};

function ConfirmDialog({ open, titulo, mensagem, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent><Typography>{mensagem}</Typography></DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onConfirm} color="error" variant="contained">Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}

function CursosLista() {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (filtroBusca) params.busca = filtroBusca;
      const data = await listarCursos(params);
      setCursos(data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar cursos');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, [filtroStatus, filtroBusca]);

  const handleDeletar = async () => {
    if (!confirmDel) return;
    try {
      await deletarCurso(confirmDel.id);
      setConfirmDel(null);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao deletar curso');
      setConfirmDel(null);
    }
  };

  const formatarData = (data) => {
    if (!data) return '—';
    return new Date(data + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  return (
    <>
      <Header />
      <ContextNavBar context="cursos" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
        <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Cursos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/cursos/novo')}>
          Novo Curso
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar cursos..."
          value={filtroBusca}
          onChange={e => setFiltroBusca(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filtroStatus} label="Status" onChange={e => setFiltroStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
              <MenuItem key={k} value={k}><Chip label={lbl} color={STATUS_COLOR[k]} size="small" /></MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : erro ? (
        <Alert severity="error">{erro}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Ministério</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Período</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Vagas</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Inscrições</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {cursos.map((curso) => (
                <TableRow
                  key={curso.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/cursos/${curso.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{curso.titulo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{curso.ministerio_nome || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatarData(curso.data_inicio)} — {formatarData(curso.data_fim)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABEL[curso.status] || curso.status} color={STATUS_COLOR[curso.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{curso.vagas_maximas || 'Ilimitado'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{curso.total_inscricoes ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => navigate(`/cursos/${curso.id}/editar`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" color="error" onClick={() => setConfirmDel(curso)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {cursos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhum curso encontrado.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        titulo="Deletar curso"
        mensagem={`Deletar "${confirmDel?.titulo}"? Todos os dados vinculados serão removidos.`}
        onConfirm={handleDeletar}
        onCancel={() => setConfirmDel(null)}
      />
    </Container>
      <Footer />
    </>
  );
}

export default CursosLista;
